import React, { useState, useEffect, useCallback } from 'react';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { obtenerBalance } from '../api/backend_v2';
import { formatearMoneda } from '../utils/moneda';
import { ChartBarIcon } from '@heroicons/react/24/outline';

// ─── Constantes ─────────────────────────────────────────────────────────────
const PERIODOS = [
  { value: 'mensual', label: 'Mensual' },
  { value: 'trimestral', label: 'Trimestral' },
  { value: 'semestral', label: 'Semestral' },
  { value: 'anual', label: 'Anual' },
  { value: 'interanual', label: 'Interanual' },
];

const COLORES = {
  ingresos: '#22c55e',
  egresos: '#ef4444',
  balance: '#3b82f6',
};

// ─── Tooltip personalizado ───────────────────────────────────────────────────
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const ingresos = payload.find((p) => p.dataKey === 'ingresos')?.value ?? 0;
  const egresos = payload.find((p) => p.dataKey === 'egresos')?.value ?? 0;
  const balance = payload.find((p) => p.dataKey === 'balance')?.value ?? 0;
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm">
      <p className="font-semibold text-gray-800 mb-2">{label}</p>
      <p className="text-green-600">↑ Ingresos: {formatearMoneda(ingresos)}</p>
      <p className="text-red-500">↓ Egresos: {formatearMoneda(egresos)}</p>
      <hr className="my-1 border-gray-200" />
      <p className={`font-semibold ${balance >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>
        {balance >= 0 ? '=' : '⚠'} Balance: {formatearMoneda(balance)}
      </p>
    </div>
  );
};

// ─── Tarjeta de resumen ──────────────────────────────────────────────────────
const TarjetaResumen = ({ label, valor, color, subtitulo }) => (
  <div className={`bg-white rounded-xl border-l-4 shadow-sm p-5 ${color}`}>
    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</p>
    <p className="text-2xl font-bold mt-1">{formatearMoneda(valor)}</p>
    {subtitulo && <p className="text-xs text-gray-400 mt-1">{subtitulo}</p>}
  </div>
);

// ─── Componente principal ────────────────────────────────────────────────────
const Balance = () => {
  const [tipoPeriodo, setTipoPeriodo] = useState('mensual');
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { tipo_periodo: tipoPeriodo };
      if (fechaInicio) params.fecha_inicio = fechaInicio;
      if (fechaFin) params.fecha_fin = fechaFin;
      const result = await obtenerBalance(params);
      setData(result);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [tipoPeriodo, fechaInicio, fechaFin]);

  useEffect(() => { cargar(); }, [cargar]);

  const periodos = data?.periodos ?? [];
  const totales = data?.totales ?? { ingresos: 0, egresos: 0, balance: 0, trabajos: 0 };

  // Datos para el gráfico — abreviamos labels largos en pantallas chicas
  const chartData = periodos.map((p) => ({
    label: p.label,
    ingresos: p.ingresos,
    egresos: p.egresos,
    balance: p.balance,
  }));

  const formatYAxis = (value) => {
    if (Math.abs(value) >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
    if (Math.abs(value) >= 1_000) return `$${(value / 1_000).toFixed(0)}k`;
    return `$${value}`;
  };

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900"><ChartBarIcon className="h-6 w-6 inline-block mr-2" /> Balance Financiero</h1>
        <p className="text-gray-500 text-sm mt-1">Comparativa de ingresos y egresos por período</p>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex flex-wrap gap-3 items-end">
          {/* Tipo período */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Agrupación</label>
            <div className="flex gap-1 flex-wrap">
              {PERIODOS.map((p) => (
                <button
                  key={p.value}
                  onClick={() => setTipoPeriodo(p.value)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    tipoPeriodo === p.value
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {/* Fechas opcionales */}
          <div className="flex gap-2 items-end ml-auto flex-wrap">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Desde</label>
              <input
                type="date"
                value={fechaInicio}
                onChange={(e) => setFechaInicio(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Hasta</label>
              <input
                type="date"
                value={fechaFin}
                onChange={(e) => setFechaFin(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            {(fechaInicio || fechaFin) && (
              <button
                onClick={() => { setFechaInicio(''); setFechaFin(''); }}
                className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50"
              >
                Limpiar
              </button>
            )}
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 mb-6 text-sm">
          {error}
        </div>
      )}

      {/* Tarjetas de totales */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <TarjetaResumen
          label="Total Ingresos"
          valor={totales.ingresos}
          color="border-green-500"
          subtitulo={`${totales.trabajos} trabajos`}
        />
        <TarjetaResumen
          label="Total Egresos"
          valor={totales.egresos}
          color="border-red-500"
          subtitulo={`${totales.cantidad_egresos ?? 0} registros`}
        />
        <TarjetaResumen
          label="Balance Neto"
          valor={totales.balance}
          color={totales.balance >= 0 ? 'border-blue-500' : 'border-orange-500'}
          subtitulo={totales.balance >= 0 ? 'Resultado positivo' : 'Resultado negativo'}
        />
        <div className="bg-white rounded-xl border-l-4 border-purple-500 shadow-sm p-5">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Margen</p>
          <p className="text-2xl font-bold mt-1 text-purple-600">
            {totales.ingresos > 0 ? `${((totales.balance / totales.ingresos) * 100).toFixed(1)}%` : '-'}
          </p>
          <p className="text-xs text-gray-400 mt-1">Sobre ingresos totales</p>
        </div>
      </div>

      {/* Gráfico */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
        <h2 className="text-base font-semibold text-gray-800 mb-4">Ingresos vs Egresos</h2>
        {loading ? (
          <div className="h-80 flex items-center justify-center text-gray-400">Cargando...</div>
        ) : periodos.length === 0 ? (
          <div className="h-80 flex items-center justify-center text-gray-400">
            No hay datos para el período seleccionado
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={340}>
            <ComposedChart data={chartData} margin={{ top: 10, right: 20, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 12, fill: '#6b7280' }}
                angle={periodos.length > 8 ? -35 : 0}
                textAnchor={periodos.length > 8 ? 'end' : 'middle'}
                height={periodos.length > 8 ? 55 : 30}
              />
              <YAxis tickFormatter={formatYAxis} tick={{ fontSize: 11, fill: '#6b7280' }} width={65} />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                formatter={(value) => value === 'ingresos' ? 'Ingresos' : value === 'egresos' ? 'Egresos' : 'Balance'}
              />
              <ReferenceLine y={0} stroke="#9ca3af" strokeDasharray="3 3" />
              <Bar dataKey="ingresos" fill={COLORES.ingresos} radius={[3, 3, 0, 0]} maxBarSize={60} />
              <Bar dataKey="egresos" fill={COLORES.egresos} radius={[3, 3, 0, 0]} maxBarSize={60} />
              <Line
                type="monotone"
                dataKey="balance"
                stroke={COLORES.balance}
                strokeWidth={2.5}
                dot={{ r: 4, fill: COLORES.balance }}
                activeDot={{ r: 6 }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Tabla */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-800">Detalle por período</h2>
        </div>
        {loading ? (
          <div className="text-center py-10 text-gray-400 text-sm">Cargando datos...</div>
        ) : periodos.length === 0 ? (
          <div className="text-center py-10 text-gray-400 text-sm">Sin datos para mostrar</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200 text-gray-600 text-xs uppercase tracking-wide">
                  <th className="text-left px-5 py-3">Período</th>
                  <th className="text-right px-5 py-3">Trabajos</th>
                  <th className="text-right px-5 py-3 text-green-700">Ingresos</th>
                  <th className="text-right px-5 py-3 text-red-600">Egresos</th>
                  <th className="text-right px-5 py-3 text-blue-700">Balance</th>
                  <th className="text-right px-5 py-3 text-purple-700">Margen</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {[...periodos].reverse().map((p) => {
                  const margen = p.ingresos > 0 ? ((p.balance / p.ingresos) * 100).toFixed(1) : null;
                  const positivo = p.balance >= 0;
                  return (
                    <tr key={p.periodo} className="hover:bg-gray-50 transition-colors">
                      <td className="px-5 py-3 font-medium text-gray-800">{p.label}</td>
                      <td className="px-5 py-3 text-right text-gray-600">{p.trabajos}</td>
                      <td className="px-5 py-3 text-right font-semibold text-green-600">
                        {formatearMoneda(p.ingresos)}
                      </td>
                      <td className="px-5 py-3 text-right font-semibold text-red-500">
                        {formatearMoneda(p.egresos)}
                      </td>
                      <td className={`px-5 py-3 text-right font-bold ${positivo ? 'text-blue-600' : 'text-orange-600'}`}>
                        {positivo ? '+' : ''}{formatearMoneda(p.balance)}
                      </td>
                      <td className={`px-5 py-3 text-right text-sm ${margen === null ? 'text-gray-400' : positivo ? 'text-purple-600 font-medium' : 'text-orange-500 font-medium'}`}>
                        {margen !== null ? `${margen}%` : '-'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              {/* Fila de totales */}
              <tfoot>
                <tr className="bg-gray-50 border-t-2 border-gray-300 font-bold text-gray-800">
                  <td className="px-5 py-3">TOTAL</td>
                  <td className="px-5 py-3 text-right">{totales.trabajos}</td>
                  <td className="px-5 py-3 text-right text-green-700">{formatearMoneda(totales.ingresos)}</td>
                  <td className="px-5 py-3 text-right text-red-600">{formatearMoneda(totales.egresos)}</td>
                  <td className={`px-5 py-3 text-right ${totales.balance >= 0 ? 'text-blue-700' : 'text-orange-600'}`}>
                    {totales.balance >= 0 ? '+' : ''}{formatearMoneda(totales.balance)}
                  </td>
                  <td className="px-5 py-3 text-right text-purple-700">
                    {totales.ingresos > 0 ? `${((totales.balance / totales.ingresos) * 100).toFixed(1)}%` : '-'}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Balance;
